/**
 * Composant Timer pour le quiz
 * Compte à rebours de 30 secondes pour chaque question
 */
import { useState, useEffect } from 'react';
import '../styles/timer.css';

/**
 * Timer - Composant de compte à rebours
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.duration - Durée du timer en secondes
 * @param {function} props.onTimeUp - Callback appelé quand le temps est écoulé
 * @param {number} props.questionIndex - Index de la question (pour réinitialiser le timer)
 * @param {function} props.onTimeChange - Callback appelé à chaque changement du temps restant
 * @returns {JSX.Element} Affichage du timer
 */
export const Timer = ({ duration = 30, onTimeUp, questionIndex, onTimeChange }) => {
    const [timeLeft, setTimeLeft] = useState(duration);

    useEffect(() => {
        // Réinitialise le timer quand la question change
        setTimeLeft(duration);
    }, [questionIndex, duration]);

    useEffect(() => {
        // Envoie le temps courant au parent si callback fourni
        if (onTimeChange) {
            onTimeChange(timeLeft);
        }

        // Si le temps est écoulé, appelle le callback
        if (timeLeft === 0) {
            onTimeUp();
            return;
        }

        // Décrémente le timer chaque seconde
        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        // Nettoyage de l'intervalle
        return () => clearInterval(timer);
    }, [timeLeft, onTimeUp, onTimeChange]);

    // Calcul du pourcentage pour l'animation
    const percentage = (timeLeft / duration) * 100;

    // Classe CSS selon le temps restant
    const getTimerClass = () => {
        if (timeLeft > 20) return 'timer-safe';
        if (timeLeft > 10) return 'timer-warning';
        return 'timer-danger';
    };

    return (
        <div className={`timer-container ${getTimerClass()}`}>
            <div className="timer-display">
                <span className="timer-icon">⏱️</span>
                <span className="timer-text">
                    {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:
                    {String(timeLeft % 60).padStart(2, '0')}
                </span>
            </div>
            <div className="timer-bar">
                <div 
                    className="timer-fill" 
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};
